import { classNames, useQuery, useState } from 'libs/react';
import { DealItemPickerBrowseTable } from './DealItemPickerBrowseTable';
import { DealItemPickerQueryBuilder } from './DealItemPickerQueryBuilder';
import { Content } from 'components/Content';
import { Error } from 'components/Error';
import { Case } from 'components/util/Case';
import { Else } from 'components/util/Else';
import { When } from 'components/util/When';
import { Waiter } from 'components/Waiter';
import { React } from 'libs/react';
import { SaleItemsCountQuery } from 'graphql/SaleItem/queries/SaleItemsCountQuery';
import { usePagedQuery } from 'libs/react/hook/usePagedQuery';
import { Pager } from 'components/pager/Pager';
import { FieldGroupsShortenedQuery } from 'graphql/FieldGroup/queries/FieldGroupsShortQuery';
import { SaleItemsListQuery } from 'graphql/Deal/queries/dealItemPicker/SaleItemListQuery';
import { useDealItemPicker } from './DealItemPicker';
import { useUser } from 'components/user/UserProvider';
import { useMemo } from 'react';
import './DealItemPickerBrowsePane.scss';
/**
 * @component DealItemPickerBrowsePane
 * @since 1.0.0
 */
export function DealItemPickerBrowsePane(props) {
    var _a, _b, _c;
    const { style } = Object.assign({ style: 'normal' }, props);
    const [search, setSearch] = useState('');
    const [filterBy, setFilterBy] = useState();
    const { itemsDisplay, deal } = useDealItemPicker();
    const user = useUser();
    // Quick quote: no deal yet, it will be created with the user's default
    // partner (applySessionDealPartner), so the catalog is scoped to it
    const partnerId = deal
        ? (_a = deal.partner) === null || _a === void 0 ? void 0 : _a.id
        : !user.isContactUser()
            ? (_c = (_b = user.user) === null || _b === void 0 ? void 0 : _b.defaultPartner) === null || _c === void 0 ? void 0 : _c.id
            : undefined;
    const [fieldGroups] = useQuery(FieldGroupsShortenedQuery, {
        variables: {
            input: {
                query: [
                    {
                        field: 'category',
                        operator: 'in',
                        value: ['PRODUCT', 'VARIANT', 'PRODUCT_CONFIGURATION']
                    }
                ]
            }
        }
    });
    const promotedFields = [];
    const fieldsStep = [];
    let fieldGroupsData = fieldGroups === null || fieldGroups === void 0 ? void 0 : fieldGroups.fieldGroups;
    if (itemsDisplay == 'ITEM_ONLY') {
        fieldGroupsData = fieldGroups === null || fieldGroups === void 0 ? void 0 : fieldGroups.fieldGroups.filter((fieldGroup) => fieldGroup.category == 'PRODUCT' ||
            fieldGroup.category == 'PRODUCT_CONFIGURATION');
    }
    if (fieldGroupsData) {
        fieldGroupsData.forEach((fieldGroup) => {
            fieldGroup.fields.forEach((field) => {
                if (field.promote) {
                    promotedFields.push(field);
                }
                if (field.useInQueryBuilder) {
                    fieldsStep.push(field);
                }
            });
        });
    }
    /**
     * @function formattedFilter
     * @since 1.0.0
     */
    const formattedFilter = () => {
        let variables = { filter: [], groupId: undefined };
        if (filterBy) {
            Object.keys(filterBy).forEach((key) => {
                var _a, _b;
                if (key === 'configuration') {
                    variables = Object.assign(Object.assign({}, variables), { configuration: filterBy[key] });
                    return;
                }
                if (key === 'group') {
                    variables = Object.assign(Object.assign({}, variables), { groupId: filterBy[key] !== 'all' ? filterBy[key] : undefined });
                    return;
                }
                if (key === 'price') {
                    return;
                }
                if (filterBy[key]) {
                    // Get category from field group (default to PRODUCT)
                    const category = (_a = fieldGroups === null || fieldGroups === void 0 ? void 0 : fieldGroups.fieldGroups.find((fieldGroup) => fieldGroup.fields.find((field) => field.id === key))) === null || _a === void 0 ? void 0 : _a.category;
                    variables = Object.assign(Object.assign({}, variables), { filter: [
                            ...((_b = variables.filter) !== null && _b !== void 0 ? _b : []),
                            {
                                field: key,
                                value: filterBy[key],
                                entity: category !== null && category !== void 0 ? category : 'PRODUCT'
                            }
                        ], filterOperator: 'AND' });
                }
            });
        }
        return variables;
    };
    const [data, ready, error, result, fetching, gotoNextPage, gotoPrevPage, gotoPage, hasNext, hasPrev, page, total] = usePagedQuery(SaleItemsListQuery, SaleItemsCountQuery, {
        variables: {
            input: Object.assign(Object.assign({ search, optionConfiguration: ['ITEM_ONLY'] }, (filterBy ? formattedFilter() : {})), { active: true, fromInternal: true, partnerId, order: 'sku:ASC' })
        },
        paging: {
            length: itemsDisplay == 'ITEM_ONLY' ? 10 : 5,
            objectType: 'saleItems',
            useQueryCount: false
        }
    });
    const filteredValues = useMemo(() => {
        var _a, _b;
        return (_b = (_a = data === null || data === void 0 ? void 0 : data.saleItems) === null || _a === void 0 ? void 0 : _a.filter((item) => !item.deletedAt)) !== null && _b !== void 0 ? _b : [];
    }, [data]);
    /**
     * @function onFilter
     * @since 1.0.0
     */
    function onFilter(params) {
        setFilterBy(params);
    }
    /**
     * @function onSearch
     * @since 1.0.0
     */
    function onSearch(search) {
        setSearch(search.trim());
    }
    const className = classNames('deal-item-picker-browse-pane', 'deal-item-picker-browse-pane--' + style, props.className);
    return (React.createElement("div", { className: className },
        React.createElement("div", { className: "deal-item-picker-browse-pane-head" },
            React.createElement(DealItemPickerQueryBuilder, { onChange: onFilter, fieldsStep: fieldsStep, filterBy: filterBy, values: filteredValues })),
        React.createElement("div", { className: "deal-item-picker-browse-pane-body" },
            React.createElement(Waiter, { until: ready, renderWhileLoading: false },
                React.createElement(When, null,
                    React.createElement(Case, { is: error },
                        React.createElement(Content, null,
                            React.createElement(Error, { title: "Ooops!", infos: "An error occured while trying to perform an action.", picto: "fal bug", error: error }))),
                    React.createElement(Else, null,
                        React.createElement(DealItemPickerBrowseTable, { values: filteredValues, fetching: fetching, onSearch: onSearch, promotedFields: promotedFields, filterBy: filterBy, itemsDisplay: itemsDisplay }),
                        React.createElement(Pager, { value: page, total: total, hasNext: hasNext, hasPrev: hasPrev, onNext: gotoNextPage, onPrev: gotoPrevPage, onPage: gotoPage })))))));
}
